import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Receipt, RefreshCw, ListChecks, Filter, X, Copy, Check } from 'lucide-react';
import CursorPagination from 'components/CursorPagination';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { DEFAULT_PAGE_SIZE } from 'lib/page-size-options';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';
import {
  LEDGER_TYPE,
  TRANSACTION_STATUS,
  TRANSACTION_STATUS_LABEL,
  LEDGER_TYPE_LABEL,
  labelFor,
  transactionTypeLabel,
} from 'constants/enums';
import type { Variant } from 'types/entities';

interface LedgerItem {
  id: number;
  transaction_uuid?: string | null;
  username?: string;
  display_name?: string | null;
  external_user_id?: string | null;
  user_id?: number;
  battle_id?: number | null;
  round_id?: number | null;
  round_number?: number | null;
  table_name?: string | null;
  action_code?: string | null;
  amount: number | string;
  balance_before?: number | string | null;
  balance_after?: number | string | null;
  transaction_type: number;
  status?: number;
  created_at?: string;
  currency_code?: string | null;
  currency_symbol?: string | null;
}

interface TxnFilters {
  userId: string;
  mode: string;
  type: string;
  status: string;
  username: string;
  battleId: string;
  txnId: string;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: TxnFilters = {
  userId: '',
  mode: '',
  type: '',
  status: '',
  username: '',
  battleId: '',
  txnId: '',
  dateFrom: '',
  dateTo: '',
};

const TYPE_OPTIONS: [string, string][] = Object.entries(LEDGER_TYPE_LABEL)
  .filter(([code]) => Number(code) !== LEDGER_TYPE.ADMIN_ADJUSTMENT)
  .map(([code, label]) => [code, label] as [string, string]);

const STATUS_OPTIONS: [string, string][] = [
  ['', 'All statuses'],
  ...Object.entries(TRANSACTION_STATUS_LABEL).map(([code, label]) => [code, label] as [string, string]),
];

function fullTxnId(uuid: string | null | undefined, fallbackId?: number): string {
  if (uuid?.trim()) return uuid.trim();
  if (fallbackId != null) return String(fallbackId);
  return '—';
}

function TxnIdCell({ uuid, fallbackId }: { uuid?: string | null; fallbackId?: number }) {
  const [copied, setCopied] = useState(false);
  const full = fullTxnId(uuid, fallbackId);
  const canCopy = full !== '—';

  const copyId = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="flex items-start gap-1.5 min-w-[220px] max-w-[320px]">
      <span className="font-mono text-[11px] text-muted break-all leading-snug">{full}</span>
      {canCopy && (
        <button
          type="button"
          onClick={() => void copyId()}
          title={copied ? 'Copied' : 'Copy transaction ID'}
          aria-label={copied ? 'Copied transaction ID' : 'Copy transaction ID'}
          className="shrink-0 p-1 rounded-md text-faint hover:text-heading hover:bg-subtle transition-colors"
        >
          {copied ? <Check size={14} strokeWidth={2.5} className="text-emerald-600" /> : <Copy size={14} strokeWidth={2} />}
        </button>
      )}
    </div>
  );
}

const money = (symbol: string, value: number | string | null | undefined) =>
  `${symbol}${parseFloat(String(value ?? 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function buildQueryParams(filters: TxnFilters) {
  const params = new URLSearchParams();
  if (filters.userId.trim()) {
    params.set('userId', filters.userId.trim());
  } else if (filters.username.trim()) {
    params.set('username', filters.username.trim());
  }
  if (filters.type) params.set('type', filters.type);
  if (filters.status) params.set('status', filters.status);
  if (filters.mode) params.set('variantId', filters.mode);
  if (filters.battleId.trim()) params.set('battleId', filters.battleId.trim());
  if (filters.txnId.trim()) params.set('txnId', filters.txnId.trim());
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  return params;
}

function countActiveFilters(filters: TxnFilters): number {
  let n = 0;
  if (filters.userId.trim() || filters.username.trim()) n += 1;
  if (filters.mode) n += 1;
  if (filters.type) n += 1;
  if (filters.status) n += 1;
  if (filters.battleId.trim()) n += 1;
  if (filters.txnId.trim()) n += 1;
  if (filters.dateFrom) n += 1;
  if (filters.dateTo) n += 1;
  return n;
}

function filtersFromPlayerUrl(search: string): TxnFilters | null {
  const params = new URLSearchParams(search);
  const userId = params.get('userId') || '';
  if (!userId) return null;
  return {
    ...EMPTY_FILTERS,
    userId,
    username: params.get('username') || '',
  };
}

const WalletMonitoring = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [draftFilters, setDraftFilters] = useState<TxnFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<TxnFilters>(EMPTY_FILTERS);
  const [playerFilterLabel, setPlayerFilterLabel] = useState('');
  const [gameModes, setGameModes] = useState<Variant[]>([]);

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildQueryParams(appliedFilters), cursor, pageSize);
    const res = await api.get(`${API_ENDPOINTS.ADMIN.WALLET.GET_LEDGER}?${params.toString()}`);
    const meta = parseCursorPagination(res);
    return {
      items: res.success && Array.isArray(res.data) ? (res.data as LedgerItem[]) : [],
      nextCursor: meta.nextCursor,
      hasMore: meta.hasMore,
      total: meta.total,
      totalPages: meta.totalPages,
    };
  }, [appliedFilters, pageSize]);

  const {
    page,
    items: ledger,
    loading,
    hasMore,
    total,
    totalPages,
    limit,
    resetAndLoad,
    refreshCurrent,
    goFirst,
    goToPage,
    goLast,
  } = useCursorListPage(fetchPage, pageSize);

  useEffect(() => {
    api.get<Variant[]>(API_ENDPOINTS.ADMIN.VARIANTS.GET_ALL)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setGameModes(res.data as Variant[]);
        }
      })
      .catch((err) => console.error('Failed to fetch game modes:', err));
  }, []);

  useEffect(() => {
    const playerFilters = filtersFromPlayerUrl(location.search);
    if (playerFilters) {
      setDraftFilters(playerFilters);
      setAppliedFilters(playerFilters);
      setPlayerFilterLabel(playerFilters.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    resetAndLoad();
  }, [appliedFilters, pageSize, resetAndLoad]);

  const updateDraft = (patch: Partial<TxnFilters>) => {
    setDraftFilters((prev) => ({ ...prev, ...patch }));
  };

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPlayerFilterLabel('');
    navigate('/wallet', { replace: true });
  };

  const clearPlayerFilter = () => {
    const next = { ...appliedFilters, userId: '', username: '' };
    setDraftFilters((prev) => ({ ...prev, userId: '', username: '' }));
    setAppliedFilters(next);
    setPlayerFilterLabel('');
    navigate('/wallet', { replace: true });
  };

  const refresh = () => refreshCurrent();

  const activeFilterCount = countActiveFilters(appliedFilters);

  if (loading && ledger.length === 0) return (
    <div className="flex h-64 items-center justify-center text-muted font-medium">
      <RefreshCw className="animate-spin mr-2" size={20} /> Loading transactions...
    </div>
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="bg-card p-6 rounded-2xl border border-line flex items-center gap-3 shadow-sm">
        <div className="p-3 bg-brand-gold/15 text-brand-gold rounded-xl"><Receipt size={24} /></div>
        <div>
          <h3 className="text-xl font-bold text-heading">Transactions</h3>
          <p className="text-sm text-muted">In-game money movement across all players and battles.</p>
        </div>
      </div>

      {appliedFilters.userId && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-blue-200/80 bg-blue-500/10 px-4 py-3">
          <p className="text-sm text-body">
            Showing transactions for{' '}
            <span className="font-bold text-heading">
              {playerFilterLabel || 'selected player'}
            </span>
          </p>
          <button
            type="button"
            onClick={clearPlayerFilter}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-line bg-card text-xs font-bold text-muted hover:text-heading hover:bg-subtle transition-colors shrink-0"
          >
            <X size={14} /> Clear player filter
          </button>
        </div>
      )}

      <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
        <div className={`${LIST_TD_CLASS} border-b border-line flex flex-col sm:flex-row sm:items-center justify-between gap-3`}>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-brand-gold" />
            <h4 className="font-bold text-heading">Filters</h4>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xxs font-bold bg-brand-gold/15 text-brand-gold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearFilters}
              disabled={activeFilterCount === 0 && countActiveFilters(draftFilters) === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-muted hover:text-heading border border-line hover:bg-subtle transition-colors disabled:opacity-50"
            >
              <X size={14} /> Clear
            </button>
            <button
              onClick={refresh}
              className="p-2 rounded-lg text-muted hover:text-heading border border-line hover:bg-subtle transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-brand-dark text-white rounded-lg text-sm font-bold hover:bg-opacity-95"
            >
              Apply Filters
            </button>
          </div>
        </div>

        <div className={`${LIST_TD_CLASS} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 border-b border-line bg-subtle/40`}>
          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Mode</label>
            <select
              value={draftFilters.mode}
              onChange={(e) => updateDraft({ mode: e.target.value })}
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
            >
              <option value="">All modes</option>
              {gameModes.map((v) => (
                <option key={v.id} value={String(v.id)}>{v.code}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Player name</label>
            <input
              value={draftFilters.username}
              onChange={(e) => updateDraft({ username: e.target.value })}
              placeholder="Search player name"
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
            />
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Type</label>
            <select
              value={draftFilters.type}
              onChange={(e) => updateDraft({ type: e.target.value })}
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
            >
              <option value="">All types</option>
              {TYPE_OPTIONS.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Status</label>
            <select
              value={draftFilters.status}
              onChange={(e) => updateDraft({ status: e.target.value })}
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
            >
              {STATUS_OPTIONS.map(([code, label]) => (
                <option key={code || 'all'} value={code}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Txn ID</label>
            <input
              value={draftFilters.txnId}
              onChange={(e) => updateDraft({ txnId: e.target.value })}
              placeholder="Partial ID"
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card font-mono"
            />
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">Battle ID</label>
            <input
              value={draftFilters.battleId}
              onChange={(e) => updateDraft({ battleId: e.target.value })}
              placeholder="e.g. 1042"
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card font-mono"
            />
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">From date</label>
            <input
              type="date"
              value={draftFilters.dateFrom}
              onChange={(e) => updateDraft({ dateFrom: e.target.value })}
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
            />
          </div>

          <div>
            <label className="block text-xxs font-bold text-muted uppercase tracking-wider mb-1.5">To date</label>
            <input
              type="date"
              value={draftFilters.dateTo}
              min={draftFilters.dateFrom || undefined}
              onChange={(e) => updateDraft({ dateTo: e.target.value })}
              className="w-full rounded-lg border-line text-sm p-2.5 focus:ring-brand-gold focus:border-brand-gold border bg-card"
            />
          </div>
        </div>

        <div className={`${LIST_TD_CLASS} border-b border-line flex items-center justify-between gap-3`}>
          <h4 className="font-bold text-heading flex items-center gap-2">
            <ListChecks className="text-brand-gold" /> Transaction Ledger
          </h4>
        </div>

        <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={LIST_TH_NO_CLASS}>No.</th>
                <th className={LIST_TH_CLASS}>Txn ID</th>
                <th className={LIST_TH_CLASS}>Player Name</th>
                <th className={LIST_TH_CLASS}>Type</th>
                <th className={LIST_TH_CLASS}>Amount</th>
                <th className={LIST_TH_CLASS}>Balance</th>
                <th className={LIST_TH_CLASS}>Battle</th>
                <th className={LIST_TH_CLASS}>Status</th>
                <th className={LIST_TH_CLASS}>Date</th>
              </tr>
            </thead>
            <tbody className={LIST_TBODY_CLASS}>
              {ledger.length > 0 ? (
                ledger.map((txn, index) => {
                  const amt = parseFloat(String(txn.amount));
                  const symbol = txn.currency_symbol || '$';
                  const typeLabel = transactionTypeLabel(txn.transaction_type, txn.action_code);
                  return (
                    <tr key={txn.id} className="hover:bg-subtle/50 transition-colors text-sm">
                      <td className={LIST_TD_NO_CLASS}>{listRowNumber(page, index, limit)}</td>
                      <td className={`${LIST_TD_CLASS}`}>
                        <TxnIdCell uuid={txn.transaction_uuid} fallbackId={txn.id} />
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        {txn.user_id ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/players/${txn.user_id}/enforcement`)}
                            className="font-semibold text-heading hover:text-brand-gold hover:underline transition-colors"
                          >
                            {txn.display_name || txn.username || '—'}
                          </button>
                        ) : (
                          <span className="font-semibold text-heading">{txn.display_name || txn.username || '—'}</span>
                        )}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-subtle text-body border border-line">
                          {typeLabel}
                        </span>
                      </td>
                      <td className={`px-5 py-3.5 whitespace-nowrap font-bold ${amt >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {amt >= 0 ? '+' : '-'}{money(symbol, Math.abs(amt))}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>
                        {txn.balance_before != null && txn.balance_after != null ? (
                          <span>{money(symbol, txn.balance_before)} → {money(symbol, txn.balance_after)}</span>
                        ) : '—'}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>
                        {txn.battle_id ? (
                          <div>
                            <button
                              type="button"
                              onClick={() => navigate(`/battles/${txn.battle_id}`)}
                              className="font-semibold text-body hover:text-brand-gold hover:underline transition-colors"
                            >
                              BTL-{txn.battle_id}
                            </button>
                            {txn.table_name && <div>{txn.table_name}</div>}
                            {txn.round_number != null && <div>R{txn.round_number}</div>}
                          </div>
                        ) : '—'}
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                        <span className={`px-2 py-0.5 rounded-full text-xxs font-semibold ${
                          txn.status === TRANSACTION_STATUS.COMPLETED ? 'bg-green-500/15 text-green-800'
                            : txn.status === TRANSACTION_STATUS.FLAGGED ? 'bg-red-500/15 text-red-800'
                            : 'bg-yellow-500/15 text-yellow-800'
                        }`}>
                          {labelFor(TRANSACTION_STATUS_LABEL, txn.status)}
                        </span>
                      </td>
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-xs text-muted`}>
                        {txn.created_at ? new Date(txn.created_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={9} className="px-6 py-10 text-center text-muted font-medium">
                    {loading ? 'Loading…' : 'No transactions match the selected filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <CursorPagination
          page={page}
          totalPages={totalPages}
          total={total}
          itemCount={ledger.length}
          limit={limit}
          hasMore={hasMore}
          loading={loading}
          onFirst={goFirst}
          onPageChange={goToPage}
          onLast={goLast}
          onLimitChange={setPageSize}
        />
      </div>
    </div>
  );
};

export default WalletMonitoring;
